import React, {
    ReactElement,
    FunctionComponent,
    useRef,
    useLayoutEffect,
    useCallback,
    useMemo,
    memo,
} from 'react'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Grid from '@sport1/react-elements/Grid'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Touchable from '@sport1/react-elements/Touchable'
import HorizontalScrollView from '../Layouts/HorizontalScroll'
import Sport1Logo from '../Theme/Logos/Sport1'
import Sport1PlusLogo from '../Theme/Logos/Sport1Plus'
import ESports1Logo from '../Theme/Logos/eSports1'
import LabelFont from '../Theme/Fonts/Label'
import LinkProps from '../Link/LinkProps'
import TimeRow from './TimeRow'
import ChannelRow from './ChannelRow'
import { TVChannel, TVProgrammType } from './types'
import { formatServerTimeToDayjs } from './utils/dayjsHelper'

export const BROADCAST_WIDTH_MULTIPLICATOR = 4

type FlatListRefType = {
    scrollToOffset: ({ offset, animated }: { offset: number; animated?: boolean }) => void
}

const getTVProgramStartTime = (channels: TVChannel[]): Dayjs => {
    const channelStartTimes: Dayjs[] = []
    channels.forEach(channel => {
        if (channel.broadcasts.length) {
            channelStartTimes.push(formatServerTimeToDayjs(channel.broadcasts[0].startTime))
        }
    })
    if (channelStartTimes.length) {
        const sortedStartTimes = channelStartTimes.sort((a, b) => a.diff(b))
        const firstStartTime = sortedStartTimes[0]
        return dayjs(
            `${firstStartTime.date()}-${
                firstStartTime.month() + 1
            }-${firstStartTime.year()} ${firstStartTime.hour()}`,
            'D-M-YYYY H'
        )
    }
    return dayjs()
}

const getTVProgramEndTime = (channels: TVChannel[]): Dayjs => {
    const channelEndTimes: Dayjs[] = []
    channels.forEach(channel => {
        const broadcasts = channel.broadcasts
        if (broadcasts.length) {
            channelEndTimes.push(formatServerTimeToDayjs(broadcasts[broadcasts.length - 1].endTime))
        }
    })
    if (channelEndTimes.length) {
        const sortedEndTimes = channelEndTimes.sort((a, b) => a.diff(b))
        const lastEndTime = sortedEndTimes[sortedEndTimes.length - 1]
        return dayjs(
            `${lastEndTime.date()}-${
                lastEndTime.month() + 1
            }-${lastEndTime.year()} ${lastEndTime.hour()}:${lastEndTime.minute()}`,
            'D-M-YYYY H:m'
        )
    }
    return dayjs()
}

const TVProgramm: FunctionComponent<TVProgrammType & LinkProps> = ({
    channels,
    onBroadcastPress,
    createBroadcastHref,
    selectedDate,
    testID,
    logoType = 'OnLight',
    webRel,
}): ReactElement => {
    dayjs.extend(customParseFormat)
    const scrollRef = useRef<FlatListRefType | null>(null)
    const currentScrollOffset = React.useRef(0)

    const tvProgramStartTime = useMemo(() => getTVProgramStartTime(channels), [channels])
    const tvProgramEndtime = useMemo(() => getTVProgramEndTime(channels), [channels])

    const getBroadcasts = useCallback((): ReactElement => {
        return (
            <Grid noExpand>
                <TimeRow startTime={tvProgramStartTime} endTime={tvProgramEndtime} />
                {channels.map(channel => {
                    const href = createBroadcastHref
                        ? createBroadcastHref({
                              streamingUrl: channel.streamingUrl,
                              channelName: channel.name,
                              channelId: channel.id,
                          })
                        : undefined
                    return (
                        <ChannelRow
                            key={channel.id}
                            channel={channel}
                            channelStartTime={tvProgramStartTime}
                            channelEndTime={tvProgramEndtime}
                            onPress={onBroadcastPress}
                            href={href}
                        />
                    )
                })}
            </Grid>
        )
    }, [channels, tvProgramEndtime, tvProgramStartTime, onBroadcastPress, createBroadcastHref])

    const getLogo = useCallback(
        (channel: TVChannel, index: number): ReactElement => {
            const { name: channelName, id: channelId, streamingUrl } = channel
            let logoAsset = <LabelFont>{channelName}</LabelFont>
            if (channelName === 'SPORT1PLUS') {
                logoAsset = <Sport1PlusLogo width={54} type={logoType} />
            } else if (channelName === 'ESPORTS1') {
                logoAsset = <ESports1Logo width={54} type={logoType} />
            } else if (channelName === 'SPORT1') {
                logoAsset = <Sport1Logo width={54} type={logoType} />
            }

            const logo = (
                <Grid
                    key={channelName}
                    noExpand
                    height={88}
                    center
                    borderTop={index === 0 ? 0 : 1}
                    borderRight={1}
                    borderColor="tahitian-4"
                >
                    {logoAsset}
                </Grid>
            )

            return onBroadcastPress || createBroadcastHref ? (
                <Touchable
                    webRel={webRel}
                    key={`${channelName}_touchable`}
                    onPress={
                        onBroadcastPress
                            ? (): void => onBroadcastPress({ channelName, channelId, streamingUrl })
                            : undefined
                    }
                    href={
                        createBroadcastHref
                            ? createBroadcastHref({ channelName, channelId, streamingUrl })
                            : undefined
                    }
                >
                    {logo}
                </Touchable>
            ) : (
                logo
            )
        },
        [createBroadcastHref, logoType, onBroadcastPress, webRel]
    )

    const onScroll = useCallback(
        ({ scrollLeft }: { scrollTop?: number; scrollLeft?: number }): void => {
            if (scrollLeft) currentScrollOffset.current = scrollLeft
        },
        []
    )

    const scrollTo = useCallback((offset: number, animated = true): void => {
        if (scrollRef.current) {
            scrollRef.current.scrollToOffset({
                offset,
                animated,
            })
        }
    }, [])

    useLayoutEffect((): void => {
        const hours = Math.floor(
            dayjs.duration(dayjs(selectedDate).diff(tvProgramStartTime)).asHours()
        )
        scrollTo(60 * BROADCAST_WIDTH_MULTIPLICATOR * hours, false)
    }, [tvProgramStartTime, scrollTo, selectedDate])

    const onScrollRight = useCallback((): void => {
        scrollTo(currentScrollOffset.current + 400)
    }, [scrollTo])

    const onScrollLeft = useCallback((): void => {
        scrollTo(Math.max(currentScrollOffset.current - 400, 0))
    }, [scrollTo])

    const keyExtractor = useCallback(() => 'tv_program_broadcasts', [])

    return (
        <Grid testID={testID} noExpand horizontal backgroundColor="pearl" position="relative">
            <Grid noExpand minWidth={86} marginTop={33}>
                {channels.map((channel, index) => getLogo(channel, index))}
            </Grid>
            <Grid noExpand overflow="hidden" width="100%">
                <HorizontalScrollView
                    innerRef={scrollRef}
                    keyExtractor={keyExtractor}
                    items={[channels]}
                    renderItem={getBroadcasts}
                    backgroundColor="pearl"
                    onScroll={onScroll}
                    testID={`${testID}-scroll-view`}
                />
            </Grid>
            <Grid noExpand position="absolute" top={0} left={0} testID={`${testID}-arrow-left`}>
                <Touchable onPress={onScrollLeft}>
                    <Grid noExpand height={33} width={50} backgroundColor="pearl" center>
                        <FontelloIcon name="scroll-left" color="onyx" fontSize={15} />
                    </Grid>
                </Touchable>
            </Grid>
            <Grid noExpand position="absolute" top={0} right={0} testID={`${testID}-arrow-right`}>
                <Touchable onPress={onScrollRight}>
                    <Grid noExpand height={33} width={50} backgroundColor="pearl" center>
                        <FontelloIcon name="scroll-right" color="onyx" fontSize={15} />
                    </Grid>
                </Touchable>
            </Grid>
        </Grid>
    )
}

export default memo(TVProgramm)
